import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { getTheme } from '../utils/theme';

const theme = getTheme();

// Eye icon components
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function PasswordInput({ style, placeholder, value, onChange, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        style={{ ...style, paddingRight: 44, width: '100%', boxSizing: 'border-box' }}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={eyeBtnStyle}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

const eyeBtnStyle = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#64748b',
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  lineHeight: 1,
};

export default function Register() {
  const navigate = useNavigate();

  // Immediate redirect if already logged in (prevents back-button issues)
  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/random', { replace: true });
    }
  }, [navigate]);

  const [form, setForm] = useState({ username: '', password: '', confirm: '' });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);

  React.useEffect(() => {
    if (!form.username) {
      setUsernameStatus(null);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await api.get(`/users/check-username/?username=${form.username}`);
        setUsernameStatus({ available: res.data.is_available, suggestions: res.data.suggestions });
      } catch (err) {
        setUsernameStatus(null);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [form.username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await api.post('/register/', { username: form.username, password: form.password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('username', res.data.username);
      localStorage.setItem('userId', res.data.id);
      navigate('/random');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.logo}>
          {theme.brandLogo.startsWith('/') ? <img src={theme.brandLogo} alt={theme.brandName} style={{ height: 40 }} /> : theme.brandLogo}
        </h1>

        <p style={styles.subtitle}>Create your account</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={{ position: 'relative' }}>
            <input style={{...styles.input, width: '100%', boxSizing: 'border-box'}} placeholder="Username" value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/ /g, '_') }))} required autoFocus />
            {usernameStatus && (
              <div style={{ fontSize: 11, marginTop: 6, color: usernameStatus.available ? '#4ade80' : '#f87171' }}>
                {usernameStatus.available ? 'Username is available' : 'Username is taken.'}
                {!usernameStatus.available && usernameStatus.suggestions?.length > 0 && (
                  <div style={{ marginTop: 2, color: '#94a3b8' }}>
                    Suggestions:{' '}
                    {usernameStatus.suggestions.map(s => (
                      <span key={s} onClick={() => setForm(f => ({...f, username: s}))} style={{ cursor: 'pointer', color: theme.colors.primary, marginRight: 6 }}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <PasswordInput
            style={styles.input}
            placeholder="Password"
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            autoComplete="new-password"
          />
          <PasswordInput
            style={styles.input}
            placeholder="Confirm Password"
            value={form.confirm}
            onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
            autoComplete="new-password"
          />
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.btn} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>
        <p style={styles.link}>
          Already have an account? <Link to="/login" style={styles.a}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0f0f13', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' },
  card: { background: '#16161d', borderRadius: 20, padding: '48px 40px', width: '100%', maxWidth: 400, border: '1px solid #2d2d3d', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' },
  logo: { margin: '0 0 4px', fontSize: 30, fontWeight: 800, color: theme.colors.primary, textAlign: 'center' },
  subtitle: { color: '#64748b', textAlign: 'center', marginBottom: 32, fontSize: 12 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  input: { padding: '12px 16px', borderRadius: 10, border: '1px solid #2d2d3d', background: '#1e1e2e', color: '#e2e8f0', fontSize: 13, outline: 'none' },
  error: { color: '#f87171', fontSize: 11, margin: 0 },
  btn: { padding: '13px', borderRadius: 10, border: 'none', background: theme.colors.gradient, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  link: { textAlign: 'center', color: '#64748b', fontSize: 12, marginTop: 20, marginBottom: 0 },
  a: { color: theme.colors.primary, textDecoration: 'none', fontWeight: 600 },
};